/**
 * 字幕示範用的逐字稿。**這是公司自己的影片與真實轉錄結果**，不是可翻譯文案，
 * 所以人名與句子不進 i18n——把它們翻成英文就不再是「這支影片轉出來長這樣」。
 *
 * 內容取自 `/site-demo` 指向的那一筆 outreach demo（share 120，人工校對過整支），
 * 也就是設定存在時這一頁會拿到的同一份資料。兩邊同源，所以打包的這一份與線上那
 * 一份不會對同一支影片講出不同的話。
 *
 * **整支影片，不是開頭幾句。** 跟播與清單都認「最後一個已經開始的句子」
 * （見 `cueIndexAt`），所以逐字稿一旦比影片短，播過最後一句之後高亮就卡住不動，
 * 影片還在播而逐字稿已經沒有下一句——在一個主打逐字稿的產品頁上，那比沒有示範
 * 更傷。代價實測過：這個檔從 3.8 KB 長到 46.1 KB，單獨 gzip 是 1.6 → 11.8 KB，
 * 而這一頁的 JS 傳輸量約 461 KB，所以多出來的約十 KB 是 +2% 量級。換掉的是一整
 * 類「為什麼逐字稿停了」的困惑。
 *
 * 那十 KB 目前是無條件付的：這個模組被 `subtitle-demo.tsx` 直接 import，設定存在
 * 時它照樣進 bundle 卻永遠用不到。要消掉得讓 fallback 改成動態載入，那是載入策略
 * 的題目，不在這個檔案裡解。
 *
 * **起點保留小數。** 這是 seek 準不準的關鍵：整數秒會把跳轉目標推開最多將近一
 * 秒，影片開口的位置就對不上高亮的那一句（原本這裡是從設計稿 `data-start` 抄來
 * 的 41 句整數，症狀就是這個）。取整只發生在最後一哩——畫面上的時間碼、退路連結
 * 的秒數，以及 YouTube 的 `start`（只吃整數）。`subtitle-demo-cues.test.ts` 釘住
 * 這條規則。
 */

export interface SubtitleCue {
  /** 起始秒數。保留小數，理由見檔頭。 */
  start: number
  /** 講者名。後端來的逐字稿講者是任意人名，沒有講者時是 null。 */
  speaker: string | null
  text: string
}

const HAI_SHUO = '海碩'
const IAN = 'Ian'

export const YOUTUBE_ID = 'opyv6Ynvjao'
export const VIDEO_POSTER = '/demo/kiao-ai-education.jpg'

export const SUBTITLE_CUES: SubtitleCue[] = [
  { start: 0.03, speaker: HAI_SHUO, text: '我們不用等到下一代' },
  { start: 1.17, speaker: HAI_SHUO, text: '整個人類的世界已經極度分化' },
  { start: 3.87, speaker: HAI_SHUO, text: '我們已經看見了' },
  { start: 5.13, speaker: HAI_SHUO, text: '三種等級的分化' },
  { start: 6.45, speaker: HAI_SHUO, text: '然後第三' },
  { start: 7.11, speaker: IAN, text: '大家好，歡迎來到《聰明學習》' },
  { start: 8.37, speaker: IAN, text: '我是 Ian' },
  { start: 8.91, speaker: IAN, text: '我們相信學習是有方法的' },
  { start: 10.65, speaker: IAN, text: '而且方法可以很聰明' },
  { start: 11.91, speaker: IAN, text: '最近網路上吵得很兇' },
  { start: 13.41, speaker: IAN, text: '現在新人好像凡事都問 AI' },
  { start: 15.51, speaker: IAN, text: '就有人開始講說' },
  { start: 16.53, speaker: IAN, text: '現在的年輕人或是現在的學生' },
  { start: 18.57, speaker: IAN, text: '是不是所謂最笨的一代' },
  { start: 20.25, speaker: IAN, text: '我蠻好奇在學界' },
  { start: 22.47, speaker: IAN, text: '就是在教育界' },
  { start: 23.73, speaker: IAN, text: '對國中、高中、國小生' },
  { start: 26.55, speaker: IAN, text: '會是怎麼樣子' },
  { start: 27.39, speaker: IAN, text: '那今天就很高興邀請到海碩老師' },
  { start: 30.51, speaker: HAI_SHUO, text: '我是葳格國際學校的總校長' },
  { start: 33.57, speaker: HAI_SHUO, text: '我們是臺中非常非常大的教育集團' },
  { start: 36.33, speaker: HAI_SHUO, text: '假設你要說 K12' },
  { start: 38.13, speaker: HAI_SHUO, text: 'K12 最大的代是我們的' },
  { start: 39.45, speaker: IAN, text: '老師我就問一個' },
  { start: 40.65, speaker: IAN, text: '直接最尖銳的問題' },
  { start: 42.21, speaker: HAI_SHUO, text: '沒問題' },
  { start: 42.69, speaker: IAN, text: '你覺不覺得現在我們正在養出' },
  { start: 44.49, speaker: IAN, text: '最笨的一代？' },
  { start: 45.33, speaker: HAI_SHUO, text: '我認為我們正在養出' },
  { start: 46.83, speaker: HAI_SHUO, text: '最聰明以及最笨的一代' },
  { start: 48.45, speaker: HAI_SHUO, text: '所以我認為這件事' },
  { start: 49.77, speaker: HAI_SHUO, text: '不能把它只放到其中一邊' },
  { start: 51.75, speaker: HAI_SHUO, text: '我們應該要把這件事極端化' },
  { start: 53.61, speaker: HAI_SHUO, text: '那首先第一個最重要的事情是' },
  { start: 55.47, speaker: HAI_SHUO, text: '我所看見的現在的時代' },
  { start: 57.51, speaker: HAI_SHUO, text: '最重要的概念是' },
  { start: 58.53, speaker: HAI_SHUO, text: '每一個時代其實都跟前一個時代' },
  { start: 60.45, speaker: HAI_SHUO, text: '長得很不一樣' },
  { start: 61.29, speaker: HAI_SHUO, text: '所以像舉個例子' },
  { start: 62.49, speaker: HAI_SHUO, text: '很多 50 年代和 60 年代的人' },
  { start: 64.29, speaker: HAI_SHUO, text: '會罵我們' },
  { start: 65.25, speaker: HAI_SHUO, text: '我是七年級生啊' },
  { start: 66.15, speaker: HAI_SHUO, text: '我講到七年級生會想你們很草莓' },
  { start: 68.07, speaker: IAN, text: '草莓族' },
  { start: 68.67, speaker: HAI_SHUO, text: '對吧，但是我覺得本身' },
  { start: 70.53, speaker: IAN, text: '每一代都有不同的標籤' },
  { start: 71.49, speaker: IAN, text: '每一代都有一個不同的標籤' },
  { start: 72.75, speaker: HAI_SHUO, text: '對，那我覺得我們在臺灣' },
  { start: 74.31, speaker: HAI_SHUO, text: '比較好的發展可能就是' },
  { start: 75.81, speaker: HAI_SHUO, text: '一代又一代我們不要去貼' },
  { start: 77.25, speaker: HAI_SHUO, text: '這個負面的標籤' },
  { start: 78.15, speaker: HAI_SHUO, text: '因為我這麼說好了' },
  { start: 79.29, speaker: HAI_SHUO, text: '你看見它有一些東西' },
  { start: 80.55, speaker: HAI_SHUO, text: '跟之前不太一樣的時候' },
  { start: 81.9, speaker: HAI_SHUO, text: '其實同時代表一件事' },
  { start: 83.73, speaker: HAI_SHUO, text: '它也有一個你大概沒有的東西' },
  { start: 85.53, speaker: IAN, text: '所以就是有跟沒有' },
  { start: 86.79, speaker: HAI_SHUO, text: '這兩件事是同時存在的' },
  { start: 88.89, speaker: HAI_SHUO, text: '但是在這個年代' },
  { start: 89.73, speaker: HAI_SHUO, text: '我大概會說的事情是' },
  { start: 90.87, speaker: HAI_SHUO, text: '如果你是一個已經很有想法' },
  { start: 92.85, speaker: HAI_SHUO, text: '然後非常知道該怎麼學習的學習者' },
  { start: 95.97, speaker: HAI_SHUO, text: '你透過真的可以比較聰明學習的方式' },
  { start: 98.88, speaker: HAI_SHUO, text: '在 AI 的時代能獲得很大的助力' },
  { start: 100.89, speaker: HAI_SHUO, text: '很大的助力就會把你' },
  { start: 102.51, speaker: HAI_SHUO, text: '完整的放到這個' },
  { start: 104.37, speaker: HAI_SHUO, text: 'continue 這個連續帶的好的這一端' },
  { start: 106.77, speaker: HAI_SHUO, text: '那剛剛會說最笨的那一端' },
  { start: 108.45, speaker: HAI_SHUO, text: '最笨的那一端我就要特別提' },
  { start: 109.95, speaker: HAI_SHUO, text: '大概會發生的狀況' },
  { start: 111.15, speaker: HAI_SHUO, text: '這個是要跟各位聽眾報告' },
  { start: 112.95, speaker: HAI_SHUO, text: '這大概是臺灣教育現在最危險的地方' },
  { start: 114.87, speaker: HAI_SHUO, text: '你就想像一件事' },
  { start: 115.92, speaker: HAI_SHUO, text: '以前我們在寫作文的時候' },
  { start: 117.15, speaker: HAI_SHUO, text: '我們其實要先把大腦裡面' },
  { start: 118.95, speaker: HAI_SHUO, text: '各式各樣的概念先全部組合起來' },
  { start: 120.81, speaker: HAI_SHUO, text: '寫作文的概念並不是單純的' },
  { start: 122.55, speaker: HAI_SHUO, text: '想到什麼就寫什麼' },
  { start: 123.93, speaker: HAI_SHUO, text: '寫作文' },
  { start: 124.739, speaker: IAN, text: '其實下筆那一段很難' },
  { start: 126.87, speaker: HAI_SHUO, text: '因為你要先思考' },
  { start: 128.34, speaker: HAI_SHUO, text: '因為思考的概念是' },
  { start: 129.51, speaker: HAI_SHUO, text: '你會先把各式各樣不同的想法' },
  { start: 131.07, speaker: HAI_SHUO, text: '在大腦裡面先 group' },
  { start: 132.45, speaker: HAI_SHUO, text: '變成我大部分想講的' },
  { start: 134.73, speaker: HAI_SHUO, text: '都是第一段的概念' },
  { start: 135.87, speaker: HAI_SHUO, text: '所以這個第一段' },
  { start: 136.77, speaker: HAI_SHUO, text: '然後這個第二段、這第三段' },
  { start: 138.24, speaker: HAI_SHUO, text: '在寫的過程之中' },
  { start: 139.26, speaker: HAI_SHUO, text: '你要用譬喻方式讓你的想法' },
  { start: 141.21, speaker: HAI_SHUO, text: '更容易瞭解還要舉例' },
  { start: 142.65, speaker: HAI_SHUO, text: '這件事情事實上對大腦的認知' },
  { start: 144.93, speaker: HAI_SHUO, text: '是非常非常有幫助的' },
  { start: 146.31, speaker: HAI_SHUO, text: '現在的狀況完全不一樣' },
  { start: 147.69, speaker: HAI_SHUO, text: '現在的小孩按兩下文章就出來了' },
  { start: 149.85, speaker: HAI_SHUO, text: '所以他大腦基本上' },
  { start: 151.23, speaker: HAI_SHUO, text: '是可以沒有經過任何練習的' },
  { start: 153.39, speaker: HAI_SHUO, text: '但想像一下如果今天我兩個學生' },
  { start: 155.91, speaker: HAI_SHUO, text: '假設我有小明跟小王好了' },
  { start: 158.25, speaker: HAI_SHUO, text: '假設小明從頭到尾在做的事情' },
  { start: 160.47, speaker: HAI_SHUO, text: '就是他文章一出來他就按兩下' },
  { start: 162.78, speaker: HAI_SHUO, text: '或者參考一下大綱' },
  { start: 164.13, speaker: HAI_SHUO, text: '然後直接把文章寫出來' },
  { start: 165.6, speaker: HAI_SHUO, text: '他的大腦就用得比較少' },
  { start: 167.19, speaker: HAI_SHUO, text: '那假設另外一隻小王' },
  { start: 168.69, speaker: HAI_SHUO, text: '假設小王他的做法是' },
  { start: 170.19, speaker: HAI_SHUO, text: '從頭到尾他先自己寫了一篇' },
  { start: 172.05, speaker: HAI_SHUO, text: '寫完一篇之後再把這個東西' },
  { start: 173.67, speaker: HAI_SHUO, text: '丟到 AI 裡面' },
  { start: 174.69, speaker: HAI_SHUO, text: '然後去問那個 AI' },
  { start: 175.83, speaker: HAI_SHUO, text: '你覺得我什麼地方寫得好' },
  { start: 177.21, speaker: HAI_SHUO, text: '什麼地方寫得不好' },
  { start: 178.05, speaker: HAI_SHUO, text: 'AI 再給他一個建議' },
  { start: 179.22, speaker: HAI_SHUO, text: '他再自己用人力的方式再寫一次' },
  { start: 181.62, speaker: HAI_SHUO, text: '在這樣的狀況之下' },
  { start: 182.64, speaker: HAI_SHUO, text: '我想你不用是教育的專家' },
  { start: 184.95, speaker: HAI_SHUO, text: '你也知道誰會比較好' },
  { start: 186.63, speaker: HAI_SHUO, text: '所以真正的概念不會是' },
  { start: 188.31, speaker: HAI_SHUO, text: '這會不會是什麼樣的一代' },
  { start: 189.93, speaker: HAI_SHUO, text: '那什麼樣的一代' },
  { start: 190.77, speaker: HAI_SHUO, text: '我們就會把同一個世代' },
  { start: 191.85, speaker: HAI_SHUO, text: '打成一個樣子' },
  { start: 192.99, speaker: HAI_SHUO, text: '但我可以非常確定的說' },
  { start: 195.15, speaker: HAI_SHUO, text: '我們已經活在一個差距極度擴大' },
  { start: 198.72, speaker: HAI_SHUO, text: '而且極度擴大的過程中' },
  { start: 200.25, speaker: HAI_SHUO, text: '還要特別講一件事' },
  { start: 201.27, speaker: HAI_SHUO, text: '想像一下這邊的這兩種人' },
  { start: 203.25, speaker: HAI_SHUO, text: '比較沒有用 AI 的人' },
  { start: 204.93, speaker: HAI_SHUO, text: '或不知道我的下面的這群人' },
  { start: 207.69, speaker: HAI_SHUO, text: '我還要把他分成幾個不同的等級' },
  { start: 209.67, speaker: HAI_SHUO, text: '完全沒有使用 AI 的人' },
  { start: 211.59, speaker: HAI_SHUO, text: '使用 AI 使用錯誤的人' },
  { start: 213.39, speaker: HAI_SHUO, text: '或者是我好想用 AI' },
  { start: 215.52, speaker: HAI_SHUO, text: '但是我沒有專業版 AI 的資源的人' },
  { start: 218.67, speaker: IAN, text: 'OK' },
  { start: 218.85, speaker: HAI_SHUO, text: '對吧，這其實是三個不同的等級' },
  { start: 220.98, speaker: HAI_SHUO, text: '但這邊就有三種不同的等級' },
  { start: 222.63, speaker: HAI_SHUO, text: '這邊的三種等級是' },
  { start: 223.77, speaker: HAI_SHUO, text: '我不但知道怎麼用 AI' },
  { start: 225.57, speaker: HAI_SHUO, text: '我還有專業版的 AI' },
  { start: 226.77, speaker: HAI_SHUO, text: '甚至我還有 max 版的 AI' },
  { start: 228.51, speaker: HAI_SHUO, text: '所以這兩者的差距' },
  { start: 230.19, speaker: IAN, text: '還有十個 max 版的 AI' },
  { start: 231.33, speaker: HAI_SHUO, text: '對，所以這兩者的差距' },
  { start: 232.89, speaker: HAI_SHUO, text: '不會只有一點點' },
  { start: 233.79, speaker: HAI_SHUO, text: '這差距會是會差非常非常多' },
  { start: 236.49, speaker: IAN, text: '老師我剛才我想問一下' },
  { start: 237.75, speaker: IAN, text: '你剛剛不太會用的那個第一層跟第二層' },
  { start: 240.51, speaker: IAN, text: '有沒有差別' },
  { start: 241.35, speaker: IAN, text: '比如說一個叫' },
  { start: 242.43, speaker: IAN, text: '它沒有辦法用' },
  { start: 243.63, speaker: HAI_SHUO, text: '我告訴你那絕對不是' },
  { start: 244.59, speaker: IAN, text: '會不會反而用了用錯' },
  { start: 246.45, speaker: IAN, text: '反而比沒得用還差' },
  { start: 248.19, speaker: HAI_SHUO, text: '我最害怕的就是有人告訴我說' },
  { start: 250.41, speaker: HAI_SHUO, text: '就是我們在聊天的時候' },
  { start: 251.67, speaker: HAI_SHUO, text: '都會講說有啊' },
  { start: 253.05, speaker: HAI_SHUO, text: '我知道什麼是 AI 啊' },
  { start: 254.13, speaker: HAI_SHUO, text: 'ChatGPT 我都有跟它聊天耶' },
  { start: 256.17, speaker: HAI_SHUO, text: '就是我每次聽到這種' },
  { start: 257.37, speaker: HAI_SHUO, text: '這事實上是我最害怕的' },
  { start: 258.87, speaker: HAI_SHUO, text: '為什麼呢' },
  { start: 259.53, speaker: HAI_SHUO, text: '就是我這麼說好了' },
  { start: 261.15, speaker: HAI_SHUO, text: '假設我是個胖子' },
  { start: 262.89, speaker: HAI_SHUO, text: '我如果不知道我是個胖子' },
  { start: 264.63, speaker: HAI_SHUO, text: '那我可能還蠻快樂的' },
  { start: 265.95, speaker: HAI_SHUO, text: '那如果今天我事實上是個胖子' },
  { start: 268.98, speaker: HAI_SHUO, text: '但我對於我胖的這些方式' },
  { start: 270.93, speaker: HAI_SHUO, text: '還有錯誤的認知' },
  { start: 272.07, speaker: HAI_SHUO, text: '假設我說我胖就是因為這個' },
  { start: 274.89, speaker: HAI_SHUO, text: '我每天會曬到太陽所以我胖' },
  { start: 277.71, speaker: HAI_SHUO, text: '就是錯誤的認知嘛' },
  { start: 279.39, speaker: HAI_SHUO, text: '所以與其都沒有做' },
  { start: 281.49, speaker: HAI_SHUO, text: '我這樣都沒有做' },
  { start: 282.63, speaker: HAI_SHUO, text: '你本來就該死囉' },
  { start: 284.37, speaker: HAI_SHUO, text: '就是完全沒辦法嘛' },
  { start: 286.17, speaker: HAI_SHUO, text: '但完全沒辦法跟有做了一點' },
  { start: 288.45, speaker: HAI_SHUO, text: '但是用錯的人' },
  { start: 289.41, speaker: HAI_SHUO, text: '我覺得那個更可惜耶' },
  { start: 290.73, speaker: IAN, text: '那至少他寫作文還會自己想' },
  { start: 292.83, speaker: HAI_SHUO, text: '對' },
  { start: 293.37, speaker: IAN, text: '他完全沒用' },
  { start: 294.09, speaker: HAI_SHUO, text: '完全沒用就至少會自己想嘛' },
  { start: 295.59, speaker: HAI_SHUO, text: '所以完全沒用' },
  { start: 296.85, speaker: HAI_SHUO, text: '至少享受了傳統方式的好' },
  { start: 299.55, speaker: HAI_SHUO, text: '所以我覺得你說下一代會不會更笨' },
  { start: 301.89, speaker: HAI_SHUO, text: '我大概不會用更笨這方式說' },
  { start: 303.42, speaker: HAI_SHUO, text: '但我可以跟你保證一件事' },
  { start: 304.95, speaker: HAI_SHUO, text: '我們不用等到下一代' },
  { start: 306.96, speaker: HAI_SHUO, text: '在 AI 現在大概三年了' },
  { start: 308.79, speaker: HAI_SHUO, text: '三年多一點' },
  { start: 309.9, speaker: HAI_SHUO, text: '在三年多一點的此刻' },
  { start: 311.61, speaker: HAI_SHUO, text: '整個人類的世界已經極度分化' },
  { start: 314.7, speaker: HAI_SHUO, text: '而且因為我每一年' },
  { start: 316.17, speaker: HAI_SHUO, text: '都有去參加人工智慧的年會' },
  { start: 318.03, speaker: HAI_SHUO, text: '我們已經看見了三種等級的分化' },
  { start: 320.79, speaker: HAI_SHUO, text: '然後第三種' },
  { start: 321.51, speaker: HAI_SHUO, text: '我們已經在第二種等級之間' },
  { start: 323.37, speaker: HAI_SHUO, text: '然後第三個已經進行式了' },
  { start: 325.05, speaker: HAI_SHUO, text: '我們一開始看見的是' },
  { start: 326.67, speaker: HAI_SHUO, text: '個人等級 individual level' },
  { start: 328.53, speaker: HAI_SHUO, text: '個人等級的分化' },
  { start: 329.55, speaker: HAI_SHUO, text: '你會看到有用 AI 的' },
  { start: 331.05, speaker: HAI_SHUO, text: '和沒有用 AI 的' },
  { start: 332.13, speaker: HAI_SHUO, text: '你再來已經看到這兩三年' },
  { start: 334.23, speaker: HAI_SHUO, text: '從去年開始人工智慧年會' },
  { start: 336.03, speaker: HAI_SHUO, text: '已經很認真在談這件事' },
  { start: 337.5, speaker: HAI_SHUO, text: '有 AI 化的公司' },
  { start: 338.91, speaker: HAI_SHUO, text: '和沒有 AI 化的公司' },
  { start: 340.17, speaker: HAI_SHUO, text: '這些公司非常認真地' },
  { start: 341.91, speaker: HAI_SHUO, text: '研究了自己的流程' },
  { start: 343.23, speaker: HAI_SHUO, text: '建構了自己 AI 化的資料庫' },
  { start: 344.97, speaker: HAI_SHUO, text: '知道什麼要 AI' },
  { start: 346.23, speaker: HAI_SHUO, text: '什麼不要 AI' },
  { start: 347.07, speaker: HAI_SHUO, text: '什麼東西要留給人' },
  { start: 348.36, speaker: HAI_SHUO, text: '什麼人絕對都不能砍' },
  { start: 349.65, speaker: HAI_SHUO, text: '那是第二個等級' },
  { start: 350.55, speaker: HAI_SHUO, text: '那再往上一層是什麼呢' },
  { start: 352.41, speaker: HAI_SHUO, text: '就是有 AI 的國家' },
  { start: 353.61, speaker: HAI_SHUO, text: '和沒有 AI 的國家' },
  { start: 355.29, speaker: HAI_SHUO, text: '然後我必須很誠實的說' },
  { start: 356.67, speaker: HAI_SHUO, text: '你現在把所有全世界' },
  { start: 358.17, speaker: HAI_SHUO, text: '你把所有國家攤開來' },
  { start: 359.79, speaker: HAI_SHUO, text: '你已經會非常明顯的看得見' },
  { start: 362.37, speaker: HAI_SHUO, text: '誰是 AI 的領先班' },
  { start: 363.87, speaker: HAI_SHUO, text: '誰不是 AI 的領先班' },
  { start: 365.37, speaker: IAN, text: '以國家層級' },
  { start: 366.81, speaker: IAN, text: '會不會只剩中國跟美國' },
  { start: 368.67, speaker: IAN, text: '有辦法訓練自己的 AI' },
  { start: 371.25, speaker: HAI_SHUO, text: '我覺得狀況是這樣' },
  { start: 372.39, speaker: HAI_SHUO, text: '我覺得大家去注意中國和美國' },
  { start: 374.07, speaker: HAI_SHUO, text: '絕對是正確的' },
  { start: 374.85, speaker: HAI_SHUO, text: '然後這邊我其實想' },
  { start: 375.87, speaker: HAI_SHUO, text: '我想極力呼籲一件事' },
  { start: 377.91, speaker: HAI_SHUO, text: '就是中國的 AI' },
  { start: 379.95, speaker: HAI_SHUO, text: '我在三年前' },
  { start: 381.15, speaker: HAI_SHUO, text: '我三年前在那個臺大演講的時候' },
  { start: 383.22, speaker: HAI_SHUO, text: '在臺下就有人問了' },
  { start: 384.45, speaker: HAI_SHUO, text: '一個很有趣的問題' },
  { start: 385.83, speaker: HAI_SHUO, text: '所以李校長你覺得' },
  { start: 387.69, speaker: HAI_SHUO, text: '中國 AI 會比較差嗎？' },
  { start: 388.89, speaker: HAI_SHUO, text: '我那時候說絕對不會' },
  { start: 390.57, speaker: HAI_SHUO, text: '中國有這麼多的使用者' },
  { start: 392.25, speaker: HAI_SHUO, text: '所以中國的 AI 絕對會是' },
  { start: 393.99, speaker: HAI_SHUO, text: '世界上數一數二強的 AI' },
  { start: 395.37, speaker: HAI_SHUO, text: '所以你現在看到的所謂的' },
  { start: 397.29, speaker: HAI_SHUO, text: '就是開源模型' },
  { start: 398.19, speaker: HAI_SHUO, text: '全部來自中國' },
  { start: 398.97, speaker: HAI_SHUO, text: '第一名到第十名' },
  { start: 399.93, speaker: HAI_SHUO, text: '一半以上全部都是他們的' },
  { start: 401.37, speaker: HAI_SHUO, text: '所以中國的 AI 會很特化' },
  { start: 404.13, speaker: HAI_SHUO, text: '那會不會強' },
  { start: 405.09, speaker: HAI_SHUO, text: '保證超強' },
  { start: 405.93, speaker: HAI_SHUO, text: '所以像舉個例子' },
  { start: 406.95, speaker: HAI_SHUO, text: '現在在模仿影音' },
  { start: 408.33, speaker: HAI_SHUO, text: '輸出短影音的最強的' },
  { start: 409.71, speaker: HAI_SHUO, text: '全部第一名到第十名' },
  { start: 411.21, speaker: HAI_SHUO, text: '全部都是中國的' },
  { start: 411.81, speaker: HAI_SHUO, text: '所以根本沒什麼好談' },
  { start: 412.83, speaker: HAI_SHUO, text: '它有這麼多的資料下去做訓練' },
  { start: 414.39, speaker: HAI_SHUO, text: '你跟我說它會輸' },
  { start: 415.53, speaker: HAI_SHUO, text: '我是不相信' },
  { start: 416.49, speaker: HAI_SHUO, text: '那最好的科技' },
  { start: 418.68, speaker: HAI_SHUO, text: '在科技面 technology 的這件事上面' },
  { start: 420.87, speaker: HAI_SHUO, text: '美國現在依然是冠軍' },
  { start: 422.49, speaker: HAI_SHUO, text: '所以你要比力量最強的一樣是美國' },
  { start: 425.22, speaker: HAI_SHUO, text: '但你要比應用最強的' },
  { start: 426.93, speaker: HAI_SHUO, text: '還真的是中國' },
  { start: 428.19, speaker: HAI_SHUO, text: '所以中國最強的事情就兩個' },
  { start: 429.81, speaker: HAI_SHUO, text: '第一個應用出來速度真的太快' },
  { start: 432.42, speaker: HAI_SHUO, text: '而且真的太捲了' },
  { start: 434.49, speaker: HAI_SHUO, text: '再來' },
  { start: 434.91, speaker: IAN, text: '很便宜' },
  { start: 435.39, speaker: HAI_SHUO, text: '對' },
  { start: 435.87, speaker: HAI_SHUO, text: '所以就是太便宜所以很捲' },
  { start: 437.43, speaker: HAI_SHUO, text: '那很捲的過程就會發現' },
  { start: 439.11, speaker: HAI_SHUO, text: '它出產品的速度和出產品的成本' },
  { start: 441.63, speaker: HAI_SHUO, text: '會落在一個臺灣' },
  { start: 443.13, speaker: HAI_SHUO, text: '我們臺灣的夥伴很難想像的地方' },
  { start: 445.83, speaker: HAI_SHUO, text: '所以在這個過程之中' },
  { start: 447.69, speaker: HAI_SHUO, text: '如果對這件事有足夠的理解' },
  { start: 449.43, speaker: HAI_SHUO, text: '你就可以找到一些切入的點' },
  { start: 451.23, speaker: HAI_SHUO, text: '然後再來中國還有第二件' },
  { start: 452.79, speaker: HAI_SHUO, text: '我覺得非常厲害的東西' },
  { start: 453.93, speaker: HAI_SHUO, text: '中國所謂的 robotics' },
  { start: 455.55, speaker: HAI_SHUO, text: '就是機械的東西' },
  { start: 456.84, speaker: HAI_SHUO, text: '他們把它叫做具身智能' },
  { start: 458.49, speaker: HAI_SHUO, text: '就是有身體的智能' },
  { start: 460.05, speaker: HAI_SHUO, text: '我其實蠻喜歡他們這個叫法的' },
  { start: 461.94, speaker: HAI_SHUO, text: '因為接下來你 AI' },
  { start: 463.35, speaker: HAI_SHUO, text: '你要對這個世界造成足夠的影響' },
  { start: 465.15, speaker: HAI_SHUO, text: '具身智能超級重要' },
  { start: 466.83, speaker: HAI_SHUO, text: '那一開始你看' },
  { start: 468.15, speaker: HAI_SHUO, text: '一開始你看到他們做出來的機器人' },
  { start: 470.85, speaker: HAI_SHUO, text: '當然有各式各樣不同的想法' },
  { start: 472.23, speaker: HAI_SHUO, text: '但我這樣講好了' },
  { start: 473.49, speaker: HAI_SHUO, text: '機器人的發展跟兩年前比起來' },
  { start: 476.13, speaker: HAI_SHUO, text: '真的已經完全不一樣' },
  { start: 477.21, speaker: HAI_SHUO, text: '我昨天看了一個非常非常重要的影片' },
  { start: 480.87, speaker: HAI_SHUO, text: '那影片是鼎泰豐' },
  { start: 482.79, speaker: HAI_SHUO, text: '然後鼎泰豐不是中國的鼎泰豐' },
  { start: 484.74, speaker: HAI_SHUO, text: '是 California 的鼎泰豐' },
  { start: 486.45, speaker: HAI_SHUO, text: '然後 California 鼎泰豐就是' },
  { start: 488.19, speaker: IAN, text: '就是那個好像號稱' },
  { start: 489.09, speaker: IAN, text: '全世界單店坪效最高的鼎泰豐' },
  { start: 491.73, speaker: HAI_SHUO, text: '最高的鼎泰豐嘛' },
  { start: 492.18, speaker: HAI_SHUO, text: '他們就是鼎泰豐的折毛巾' },
  { start: 493.89, speaker: HAI_SHUO, text: '然後就是一開始第一支機器人訓練的時候' },
  { start: 497.73, speaker: HAI_SHUO, text: '機器人折毛巾一個小時的產出量' },
  { start: 500.49, speaker: HAI_SHUO, text: '只有人類的 40%' },
  { start: 502.35, speaker: HAI_SHUO, text: '所以簡單的說' },
  { start: 503.49, speaker: HAI_SHUO, text: '折太慢了' },
  { start: 504.21, speaker: HAI_SHUO, text: '但是可以折得好喔' },
  { start: 505.74, speaker: HAI_SHUO, text: '在折得好的狀況下只有 40%' },
  { start: 507.84, speaker: HAI_SHUO, text: '但現在今年它的產能是 2.7 倍' },
  { start: 511.77, speaker: HAI_SHUO, text: '而且折得是很好的' },
  { start: 513.09, speaker: HAI_SHUO, text: '假設我們設一個公式' },
  { start: 514.59, speaker: HAI_SHUO, text: '這個公式是一個程式' },
  { start: 516.63, speaker: HAI_SHUO, text: '就是中間有一個前面的數' },
  { start: 518.13, speaker: HAI_SHUO, text: '再來中間還有第二個數嘛' },
  { start: 519.57, speaker: HAI_SHUO, text: '兩個東西相乘' },
  { start: 520.41, speaker: HAI_SHUO, text: '然後到最後得到答案' },
  { start: 521.37, speaker: HAI_SHUO, text: '科技假設我把它放在第二個' },
  { start: 523.59, speaker: HAI_SHUO, text: '人類的力量把它放在第一個' },
  { start: 525.45, speaker: HAI_SHUO, text: '我完全不用講' },
  { start: 526.65, speaker: HAI_SHUO, text: '你就知道科技會瘋狂的一直往前走' },
  { start: 528.66, speaker: HAI_SHUO, text: '在這個時代已經沒有辦法控制了' },
  { start: 530.79, speaker: HAI_SHUO, text: '我們就拿親愛的歐盟' },
  { start: 532.11, speaker: HAI_SHUO, text: '來舉一個最好的例子' },
  { start: 534.09, speaker: HAI_SHUO, text: '歐盟是全世界在 AI 上面' },
  { start: 536.13, speaker: HAI_SHUO, text: '立法立得最好的國家' },
  { start: 537.69, speaker: HAI_SHUO, text: '第一名' },
  { start: 538.65, speaker: HAI_SHUO, text: '所以它發展很慢嘛' },
  { start: 540.57, speaker: HAI_SHUO, text: '對，就是這樣啊' },
  { start: 541.83, speaker: HAI_SHUO, text: '所以我這樣講好了' },
  { start: 542.91, speaker: HAI_SHUO, text: '為什麼中國 AI 可以這麼快' },
  { start: 544.35, speaker: HAI_SHUO, text: '很簡單' },
  { start: 544.95, speaker: HAI_SHUO, text: '人家 AI 人家資料取得快' },
  { start: 546.69, speaker: HAI_SHUO, text: '因為我們都知道 AI 三本柱' },
  { start: 548.73, speaker: HAI_SHUO, text: '算力、算法、資料' },
  { start: 550.17, speaker: HAI_SHUO, text: '你就是三隻腳都要有' },
  { start: 551.85, speaker: HAI_SHUO, text: '人家資料一定世界第一名' },
  { start: 554.19, speaker: HAI_SHUO, text: '而且我這樣講好了' },
  { start: 555.39, speaker: HAI_SHUO, text: '它使用資料沒有各式各樣的限制' },
  { start: 557.79, speaker: HAI_SHUO, text: '甚至也沒有隱私' },
  { start: 558.87, speaker: HAI_SHUO, text: '跟智慧財產權的限制呢' },
  { start: 560.46, speaker: HAI_SHUO, text: '在這樣的狀況之下' },
  { start: 561.54, speaker: HAI_SHUO, text: '你跟我講它會弱' },
  { start: 562.53, speaker: HAI_SHUO, text: '我才不信' },
  { start: 563.49, speaker: HAI_SHUO, text: '歐盟是全世界第一個定下來' },
  { start: 566.19, speaker: HAI_SHUO, text: '而且現在立法已經完成' },
  { start: 567.87, speaker: HAI_SHUO, text: '而且已經執法囉' },
  { start: 569.55, speaker: HAI_SHUO, text: '18 歲以下的青少年' },
  { start: 571.95, speaker: HAI_SHUO, text: '不可以接觸 AI 的伴侶' },
  { start: 575.07, speaker: HAI_SHUO, text: '我覺得這個太重要了好不好' },
  { start: 577.68, speaker: HAI_SHUO, text: '因為我這樣講 AI 的伴侶' },
  { start: 579.15, speaker: HAI_SHUO, text: '首先第一個它的外型一定是完美的' },
  { start: 581.07, speaker: HAI_SHUO, text: '我可以自動設定自己它的聲音外型' },
  { start: 583.17, speaker: HAI_SHUO, text: '它聲音也是完美的' },
  { start: 584.25, speaker: HAI_SHUO, text: '那各位' },
  { start: 585.21, speaker: HAI_SHUO, text: '請問你跟你的伴侶吵架' },
  { start: 586.59, speaker: HAI_SHUO, text: '或你男女朋友吵架' },
  { start: 587.61, speaker: HAI_SHUO, text: '你永遠有先道歉嗎？' },
  { start: 589.17, speaker: HAI_SHUO, text: '不一定有嘛' },
  { start: 589.95, speaker: HAI_SHUO, text: 'AI 會先道歉' },
  { start: 591.63, speaker: HAI_SHUO, text: '我們攝影師這邊' },
  { start: 592.47, speaker: HAI_SHUO, text: '我們導演這邊搖頭就沒有嘛' },
  { start: 594.03, speaker: HAI_SHUO, text: '然後再來下一個狀況喔' },
  { start: 595.47, speaker: HAI_SHUO, text: '來' },
  { start: 595.71, speaker: HAI_SHUO, text: '你有百分之百記得' },
  { start: 597.21, speaker: HAI_SHUO, text: '所有你的伴侶告訴你的事嗎？' },
  { start: 599.175, speaker: HAI_SHUO, text: '我是沒有啦' },
  { start: 600.255, speaker: HAI_SHUO, text: '我不知道你有沒有' },
  { start: 600.975, speaker: HAI_SHUO, text: '對不起' },
  { start: 601.275, speaker: HAI_SHUO, text: '我先自首' },
  { start: 602.415, speaker: HAI_SHUO, text: '我是沒有啦' },
  { start: 603.195, speaker: HAI_SHUO, text: '我這樣講' },
  { start: 603.795, speaker: HAI_SHUO, text: 'AI 都會記得哦' },
  { start: 604.815, speaker: HAI_SHUO, text: 'AI 永遠先道歉' },
  { start: 605.715, speaker: HAI_SHUO, text: 'AI 記得' },
  { start: 606.195, speaker: HAI_SHUO, text: '然後還有另外一個狀況是' },
  { start: 607.155, speaker: HAI_SHUO, text: '如果你的伴侶有情緒需求' },
  { start: 608.415, speaker: HAI_SHUO, text: '你會不會放下你的一切' },
  { start: 610.155, speaker: HAI_SHUO, text: '去支持你的伴侶？' },
  { start: 611.775, speaker: HAI_SHUO, text: 'AI 會' },
  { start: 613.455, speaker: HAI_SHUO, text: '然後最重要的一個條件是' },
  { start: 615.135, speaker: HAI_SHUO, text: '為什麼我非常支持歐盟的這個法條' },
  { start: 618.315, speaker: HAI_SHUO, text: '你就講告白這件事' },
  { start: 619.995, speaker: HAI_SHUO, text: '告白這件事是我覺得' },
  { start: 621.195, speaker: HAI_SHUO, text: '人類愛情行為裡面' },
  { start: 622.275, speaker: HAI_SHUO, text: '最偉大的一件事情' },
  { start: 623.415, speaker: HAI_SHUO, text: '我拿著我的心放到你面前' },
  { start: 625.755, speaker: HAI_SHUO, text: '你可以用一句話把我的心撕碎' },
  { start: 628.515, speaker: HAI_SHUO, text: '但這是愛情美好的地方啊' },
  { start: 630.075, speaker: IAN, text: '不確定會發生什麼事' },
  { start: 631.335, speaker: HAI_SHUO, text: '那才美好嘛！' },
  { start: 632.175, speaker: HAI_SHUO, text: '如果在還沒有告白之前' },
  { start: 633.555, speaker: HAI_SHUO, text: '我就已經知道' },
  { start: 634.215, speaker: HAI_SHUO, text: '哎喲，這可能會成功' },
  { start: 635.415, speaker: HAI_SHUO, text: '或一定會成功' },
  { start: 636.615, speaker: HAI_SHUO, text: '那告白就不有趣了' },
  { start: 638.355, speaker: HAI_SHUO, text: '但如果你是 AI' },
  { start: 639.495, speaker: HAI_SHUO, text: '你可以操縱會不會成功耶' },
  { start: 641.175, speaker: HAI_SHUO, text: '所以你如果讓我們的下一代' },
  { start: 643.635, speaker: HAI_SHUO, text: '多了很多跟 AI 的伴侶' },
  { start: 645.975, speaker: HAI_SHUO, text: '或 AI 的夥伴' },
  { start: 647.115, speaker: HAI_SHUO, text: '你越習慣跟 AI 伴侶' },
  { start: 648.795, speaker: HAI_SHUO, text: '你要跟人類相處超難的耶' },
  { start: 650.895, speaker: HAI_SHUO, text: 'AI 永遠會用超級快的速度' },
  { start: 652.755, speaker: HAI_SHUO, text: '給你各式各樣不同的回應' },
  { start: 654.225, speaker: HAI_SHUO, text: '而且會給你超級清晰的回應' },
  { start: 655.935, speaker: HAI_SHUO, text: '你知道人類相處最好的事' },
  { start: 657.555, speaker: HAI_SHUO, text: '是什麼嗎？' },
  { start: 658.395, speaker: HAI_SHUO, text: '像剛才我已經從頭到尾' },
  { start: 659.655, speaker: HAI_SHUO, text: '我劈哩啪啦講了一大堆' },
  { start: 660.915, speaker: HAI_SHUO, text: '對不對？' },
  { start: 661.275, speaker: HAI_SHUO, text: '我的心裡一定會想的事情是' },
  { start: 662.895, speaker: HAI_SHUO, text: '欸，藝人現在心中不知道在想什麼' },
  { start: 664.815, speaker: IAN, text: '我就會心裡在想說' },
  { start: 666.195, speaker: IAN, text: '欸，都沒有按照這上面耶' },
  { start: 667.755, speaker: IAN, text: '哈哈哈，這樣才有趣' },
  { start: 669.795, speaker: IAN, text: '這樣才有趣啊' },
  { start: 670.695, speaker: HAI_SHUO, text: '所以你問了一個點' },
  { start: 671.595, speaker: HAI_SHUO, text: '我就劈哩啪啦噼裡啪啦先講' },
  { start: 673.095, speaker: HAI_SHUO, text: '這樣你永遠都可以把我拉回來' },
  { start: 674.595, speaker: HAI_SHUO, text: '但在這件事上面' },
  { start: 675.855, speaker: HAI_SHUO, text: '我們就要回來再講一件事' },
  { start: 677.355, speaker: HAI_SHUO, text: '歐盟哦' },
  { start: 678.015, speaker: HAI_SHUO, text: '歐盟因為這樣' },
  { start: 678.855, speaker: HAI_SHUO, text: '因為它法條很好' },
  { start: 679.995, speaker: HAI_SHUO, text: '對隱私保護很好' },
  { start: 681.075, speaker: HAI_SHUO, text: '所以它發展很慢' },
  { start: 682.635, speaker: HAI_SHUO, text: '所以在今天的這件事之上' },
  { start: 684.435, speaker: HAI_SHUO, text: '你要如何在人類的發展' },
  { start: 686.715, speaker: HAI_SHUO, text: '跟科技的發展之間' },
  { start: 688.215, speaker: HAI_SHUO, text: '取一個平衡點？' },
  { start: 689.955, speaker: HAI_SHUO, text: '它太吃智慧了' },
  { start: 691.695, speaker: HAI_SHUO, text: '然後我可以非常負責任地說' },
  { start: 693.435, speaker: HAI_SHUO, text: '反正都錄起來嘛' },
  { start: 694.335, speaker: HAI_SHUO, text: '我可以負責任地講' },
  { start: 695.235, speaker: HAI_SHUO, text: '這件事不會有標準答案' },
  { start: 698.475, speaker: HAI_SHUO, text: '它都是 choice' },
  { start: 699.795, speaker: HAI_SHUO, text: '就像是矽谷現在最有名說的事情' },
  { start: 702.225, speaker: HAI_SHUO, text: '以前人家都說 work-life balance' },
  { start: 704.715, speaker: HAI_SHUO, text: '你要在工作跟人生中取得平衡' },
  { start: 707.475, speaker: HAI_SHUO, text: '親愛的夥伴們' },
  { start: 708.975, speaker: HAI_SHUO, text: '永遠沒有 work-life balance' },
  { start: 710.655, speaker: HAI_SHUO, text: '永遠只有 work-life choice' },
  { start: 712.875, speaker: IAN, text: '完全認同' },
  { start: 713.775, speaker: HAI_SHUO, text: '真的就是這樣' },
  { start: 714.615, speaker: HAI_SHUO, text: '就是 choice' },
  { start: 715.815, speaker: HAI_SHUO, text: '我們也需要做一個 choice' },
  { start: 717.435, speaker: IAN, text: '老師你現在對 AI 是蠻瞭解的嘛' },
  { start: 719.415, speaker: IAN, text: '有三個問題' },
  { start: 720.495, speaker: IAN, text: '就是想讓想要學習 AI 的人' },
  { start: 723.285, speaker: IAN, text: '更瞭解 AI 的人知道' },
  { start: 724.695, speaker: IAN, text: '第一是' },
  { start: 725.235, speaker: IAN, text: '你自己在學 AI 的過程中' },
  { start: 726.885, speaker: IAN, text: '你覺得有沒有' },
  { start: 727.875, speaker: IAN, text: '一個很好的心法' },
  { start: 729.015, speaker: IAN, text: '你很想跟大家分享' },
  { start: 730.035, speaker: IAN, text: '你覺得對你很有效？' },
  { start: 731.205, speaker: HAI_SHUO, text: 'Try everything' },
  { start: 731.835, speaker: HAI_SHUO, text: '就是最重要的事' },
  { start: 732.555, speaker: HAI_SHUO, text: '它的變化太快了' },
  { start: 733.755, speaker: HAI_SHUO, text: '所以在變化太快的過程之中' },
  { start: 735.495, speaker: HAI_SHUO, text: '你看到什麼東西出來' },
  { start: 736.875, speaker: HAI_SHUO, text: '先試試看' },
  { start: 737.595, speaker: HAI_SHUO, text: '這個也試試' },
  { start: 738.315, speaker: HAI_SHUO, text: '那個也試試' },
  { start: 738.915, speaker: HAI_SHUO, text: '然後看到所有新的東西出來' },
  { start: 740.235, speaker: HAI_SHUO, text: '用開放的心態去帶' },
  { start: 741.615, speaker: HAI_SHUO, text: '我覺得 try everything' },
  { start: 742.815, speaker: HAI_SHUO, text: '是最重要的第一個概念' },
  { start: 743.955, speaker: HAI_SHUO, text: '什麼東西都要試試' },
  { start: 745.065, speaker: IAN, text: '第二個是' },
  { start: 745.845, speaker: IAN, text: '有沒有誤解' },
  { start: 746.835, speaker: HAI_SHUO, text: '我覺得誤解最重要的事情是' },
  { start: 748.245, speaker: HAI_SHUO, text: '以前的時代' },
  { start: 748.995, speaker: HAI_SHUO, text: '我大概學會一個技能之後' },
  { start: 750.645, speaker: HAI_SHUO, text: '我大概半年到一年' },
  { start: 751.695, speaker: HAI_SHUO, text: '我就可以先放著不用去看' },
  { start: 753.015, speaker: HAI_SHUO, text: '我現在的狀況是' },
  { start: 754.035, speaker: HAI_SHUO, text: '我大概每隔一個月' },
  { start: 755.175, speaker: HAI_SHUO, text: '我就先 review 一次' },
  { start: 756.255, speaker: HAI_SHUO, text: '我現在所有各式各樣的東西' },
  { start: 757.455, speaker: HAI_SHUO, text: '有沒有差異' },
  { start: 758.115, speaker: HAI_SHUO, text: '所以在這樣的狀況之下' },
  { start: 759.465, speaker: HAI_SHUO, text: '像使用 AI 也是' },
  { start: 760.695, speaker: HAI_SHUO, text: '之前有各式各樣設計' },
  { start: 762.375, speaker: HAI_SHUO, text: '跟 AI 互動' },
  { start: 763.635, speaker: HAI_SHUO, text: '或者設計 AI 結構的方法' },
  { start: 765.255, speaker: HAI_SHUO, text: '每次只要 AI 一改版' },
  { start: 766.695, speaker: HAI_SHUO, text: '很多東西舊版的東西' },
  { start: 767.835, speaker: HAI_SHUO, text: '就不但沒有幫助' },
  { start: 768.975, speaker: HAI_SHUO, text: '反而會拖累它的速度' },
  { start: 770.715, speaker: HAI_SHUO, text: '所以在這樣的過程之中' },
  { start: 772.215, speaker: HAI_SHUO, text: '如何適度地' },
  { start: 774.135, speaker: HAI_SHUO, text: 'Elon Musk 會一直做的事情是' },
  { start: 775.635, speaker: HAI_SHUO, text: 'Elon Musk 會一直研究流程' },
  { start: 777.075, speaker: HAI_SHUO, text: '然後一直把不需要的流程砍掉' },
  { start: 779.055, speaker: HAI_SHUO, text: '我認為在這個年代' },
  { start: 780.135, speaker: HAI_SHUO, text: '第二個最重要的事情是這個' },
  { start: 781.635, speaker: HAI_SHUO, text: '所以一個非常重要的誤解是' },
  { start: 783.525, speaker: HAI_SHUO, text: '以前的狀況是' },
  { start: 784.455, speaker: HAI_SHUO, text: '我打好一個根基' },
  { start: 785.655, speaker: HAI_SHUO, text: '把這個根基放著絕對都不動' },
  { start: 787.695, speaker: HAI_SHUO, text: '然後讓它一步一步往上' },
  { start: 789.315, speaker: HAI_SHUO, text: '我可以跟你保證' },
  { start: 790.275, speaker: HAI_SHUO, text: '現在 AI' },
  { start: 791.415, speaker: HAI_SHUO, text: '你只有你最重要' },
  { start: 792.855, speaker: HAI_SHUO, text: '要加強的能力' },
  { start: 793.695, speaker: HAI_SHUO, text: '那些一定不會動的根基' },
  { start: 794.955, speaker: HAI_SHUO, text: '就是 learning how to learn' },
  { start: 795.915, speaker: HAI_SHUO, text: '這種事情' },
  { start: 796.515, speaker: HAI_SHUO, text: '那個才是真正的根基' },
  { start: 798.015, speaker: HAI_SHUO, text: '剩下所有 AI 有關的技術' },
  { start: 799.515, speaker: HAI_SHUO, text: '你大概每次只要碰到改版' },
  { start: 801.735, speaker: HAI_SHUO, text: '你就要看一下' },
  { start: 802.395, speaker: HAI_SHUO, text: '你目前的運用方式' },
  { start: 803.415, speaker: HAI_SHUO, text: '是對的還是錯的' },
  { start: 804.315, speaker: HAI_SHUO, text: '因為太快了' },
  { start: 806.415, speaker: HAI_SHUO, text: '所以再也沒有那種' },
  { start: 808.095, speaker: HAI_SHUO, text: '我放一個東西下去' },
  { start: 809.415, speaker: HAI_SHUO, text: '然後永遠都可以繼續下去' },
  { start: 810.795, speaker: HAI_SHUO, text: '沒有這種事了' },
  { start: 811.695, speaker: HAI_SHUO, text: '所以不能再這樣想' },
  { start: 812.775, speaker: IAN, text: '如果有人要學 AI' },
  { start: 813.855, speaker: IAN, text: '什麼都不記得' },
  { start: 814.575, speaker: IAN, text: '就記得一句話' },
  { start: 815.415, speaker: IAN, text: '那你大概會跟他說什麼？' },
  { start: 816.975, speaker: HAI_SHUO, text: '想辦法先讓 AI' },
  { start: 818.355, speaker: HAI_SHUO, text: '對你自己產生幫助' },
  { start: 819.795, speaker: HAI_SHUO, text: '因為相信我' },
  { start: 821.175, speaker: HAI_SHUO, text: '我不管你今天聽了我說什麼' },
  { start: 823.455, speaker: HAI_SHUO, text: '只要 AI 對你沒有產生' },
  { start: 825.015, speaker: HAI_SHUO, text: '實質的幫助' },
  { start: 825.915, speaker: HAI_SHUO, text: '它就不會在你的世界中存在' },
  { start: 827.715, speaker: HAI_SHUO, text: '但只要 AI 對你的生命' },
  { start: 829.695, speaker: HAI_SHUO, text: '實質產生幫助了' },
  { start: 831.135, speaker: HAI_SHUO, text: '只要有第一次' },
  { start: 832.215, speaker: HAI_SHUO, text: '你只要產生幫助了' },
  { start: 833.535, speaker: HAI_SHUO, text: '你就會發現這個東西' },
  { start: 834.795, speaker: HAI_SHUO, text: '可以存在你的人生之中' },
  { start: 836.415, speaker: HAI_SHUO, text: '我的最終目標是想辦法' },
  { start: 838.755, speaker: HAI_SHUO, text: '讓臺灣越多人接觸 AI 越好' },
  { start: 841.035, speaker: HAI_SHUO, text: '想辦法讓臺灣越多人' },
  { start: 842.355, speaker: HAI_SHUO, text: '接觸 AI 越好的方式' },
  { start: 843.555, speaker: HAI_SHUO, text: '絕對不是說這很重要哦' },
  { start: 845.895, speaker: HAI_SHUO, text: '這很棒哦' },
  { start: 846.495, speaker: HAI_SHUO, text: '大家來試試看' },
  { start: 847.275, speaker: HAI_SHUO, text: '我覺得這是不會有用的' },
  { start: 848.955, speaker: HAI_SHUO, text: '一點意義也沒有' },
  { start: 850.095, speaker: HAI_SHUO, text: '我認為真正有用的方式' },
  { start: 851.775, speaker: HAI_SHUO, text: '只有一個' },
  { start: 852.495, speaker: HAI_SHUO, text: '那就是每一個人' },
  { start: 853.695, speaker: HAI_SHUO, text: '用完 AI 之後發現' },
  { start: 854.955, speaker: HAI_SHUO, text: '哇哦' },
  { start: 855.495, speaker: HAI_SHUO, text: '你看 AI 可以這樣耶' },
  { start: 856.995, speaker: HAI_SHUO, text: 'AI 對我帶來了這樣的幫助' },
  { start: 858.495, speaker: HAI_SHUO, text: '你在獲得幫助之後' },
  { start: 859.875, speaker: HAI_SHUO, text: '你自己就會想要繼續用' },
  { start: 861.975, speaker: HAI_SHUO, text: '我覺得自己想要繼續用的' },
  { start: 864.555, speaker: HAI_SHUO, text: '這個概念才是最核心的' },
  { start: 867.255, speaker: HAI_SHUO, text: '所以我想拜託的事情是' },
  { start: 869.055, speaker: HAI_SHUO, text: '你所有要做的事情' },
  { start: 870.315, speaker: HAI_SHUO, text: '請你麻煩回家之後' },
  { start: 872.475, speaker: HAI_SHUO, text: '想辦法讓你今天用這 AI' },
  { start: 875.355, speaker: HAI_SHUO, text: '讓它看見它對你的生命之中' },
  { start: 877.635, speaker: HAI_SHUO, text: '產生了某一種幫助' },
  { start: 879.195, speaker: HAI_SHUO, text: '只要這件事一發生' },
  { start: 880.575, speaker: HAI_SHUO, text: '你生命跟 AI 就會開始' },
  { start: 882.435, speaker: HAI_SHUO, text: '產生某一種結合的方式' },
  { start: 885.015, speaker: HAI_SHUO, text: '再從那個點看' },
  { start: 886.335, speaker: HAI_SHUO, text: '下一步路會在哪邊' },
  { start: 887.655, speaker: HAI_SHUO, text: '不是有一種說法叫做' },
  { start: 888.915, speaker: HAI_SHUO, text: '摸著石頭過河嗎？' },
  { start: 890.055, speaker: HAI_SHUO, text: '在摸著石頭過河的過程之中' },
  { start: 891.525, speaker: HAI_SHUO, text: '你只要知道方向大概在那邊' },
  { start: 893.055, speaker: HAI_SHUO, text: '你就是一次一顆石頭' },
  { start: 894.495, speaker: HAI_SHUO, text: '一次一顆石頭摸過去就可以了' },
  { start: 896.175, speaker: HAI_SHUO, text: '所以我們最需要的只有一件事' },
  { start: 897.975, speaker: HAI_SHUO, text: '我不想要你看到' },
  { start: 899.475, speaker: HAI_SHUO, text: '三年以後的石頭' },
  { start: 900.795, speaker: HAI_SHUO, text: '我也不需要看到' },
  { start: 901.815, speaker: HAI_SHUO, text: '你三個月以後的石頭' },
  { start: 903.315, speaker: HAI_SHUO, text: '你幫我想今天晚上' },
  { start: 904.995, speaker: HAI_SHUO, text: '那一顆是什麼就可以了' },
  { start: 906.255, speaker: IAN, text: '謝謝海碩老師' },
  { start: 907.215, speaker: HAI_SHUO, text: '不會，謝謝' },
  { start: 907.815, speaker: IAN, text: '那下次我們再一起聰明學習' },
]
